/**
 * Quoting contracts (RBAC area: `sales`).
 *
 * Every quote belongs to an opportunity — ONE quote family per opportunity, versioned
 * v1..vN. Only the LATEST version is editable/sendable; "Promote" copies an old
 * version's content into a NEW latest version (history is never rewritten). A quote has
 * twelve rich-text sections (each with an editable, removable title and page-break
 * toggles) plus a product-line snapshot rendered after a configurable section. Templates are matched to
 * opportunities by Type (service category); applying one copies its sections onto the
 * quote and its lines onto BOTH the quote snapshot and the opportunity's products.
 */
export const QUOTE_STATUSES = ['draft', 'sent', 'approved'];
export const QUOTE_STATUS_LABELS = {
    draft: 'Draft',
    sent: 'Sent',
    approved: 'Approved',
};
/** Every quote (and template) has exactly this many NUMBERED sections. */
export const QUOTE_SECTION_COUNT = 12;
/**
 * The dedicated [Legal Contract] section: a 13th slot stored alongside sections 1..12
 * that ALWAYS renders at the bottom of the document (after every numbered section and
 * line table). Unlike the numbered sections it can never be hidden, and a quote cannot
 * be SENT to the customer while it is blank.
 */
export const QUOTE_LEGAL_SECTION_INDEX = QUOTE_SECTION_COUNT + 1;
/** Default heading of the Legal Contract section. */
export const QUOTE_LEGAL_SECTION_TITLE = 'Legal Contract';
/**
 * PSA-data variables that can be typed into section text (title or body) as
 * `[Token]` — e.g. "…services to [Customer.Name]." — and are substituted with the
 * quote's live values on the PDF and the delivery page (matching is case-insensitive).
 */
export const QUOTE_TEXT_VARIABLES = [
    { token: 'Customer.Name', label: 'Customer name' },
    { token: 'Contact.FirstName', label: 'Contact first name' },
    { token: 'Contact.LastName', label: 'Contact last name' },
    { token: 'Opportunity.Name', label: 'Opportunity name' },
    { token: 'Opportunity.Type', label: 'Opportunity type' },
    { token: 'SalesRep.Name', label: 'Sales rep name' },
    { token: 'SalesRep.Email', label: 'Sales rep email' },
];
/** Billing frequency of a product-lines GROUP (whole table, not per line). */
export const QUOTE_LINES_FREQUENCIES = ['one_time', 'monthly', 'yearly'];
export const QUOTE_LINES_FREQUENCY_LABELS = {
    one_time: 'One-time',
    monthly: 'Monthly',
    yearly: 'Yearly',
};
/**
 * Where the "Approve this quote" block sits on the delivery page. Unlike the line-table
 * placements (a plain section index) this can also anchor to a line table, so it is a
 * named token rather than a number. `end` keeps the historical behaviour — the very
 * bottom of the document.
 */
export const QUOTE_APPROVAL_PLACEMENTS = [
    'end',
    'afterSection1',
    'afterSection2',
    'afterSection3',
    'afterSection4',
    'afterSection5',
    'afterSection6',
    'afterSection7',
    'afterSection8',
    'afterSection9',
    'afterSection10',
    'afterSection11',
    'afterSection12',
    'afterLines1',
    'afterLines2',
];
export const QUOTE_APPROVAL_PLACEMENT_LABELS = {
    end: 'End of document',
    afterSection1: 'After section 1',
    afterSection2: 'After section 2',
    afterSection3: 'After section 3',
    afterSection4: 'After section 4',
    afterSection5: 'After section 5',
    afterSection6: 'After section 6',
    afterSection7: 'After section 7',
    afterSection8: 'After section 8',
    afterSection9: 'After section 9',
    afterSection10: 'After section 10',
    afterSection11: 'After section 11',
    afterSection12: 'After section 12',
    afterLines1: 'After lines 1',
    afterLines2: 'After lines 2',
};
/** Anchor token for the line table of a given group (1 or 2). */
export function approvalAnchorForLineGroup(group) {
    return group === 1 ? 'afterLines1' : 'afterLines2';
}
/** Anchor token for the section at `sectionIndex` (1-based); null when out of range. */
export function approvalAnchorForSection(sectionNumber) {
    if (sectionNumber < 1 || sectionNumber > QUOTE_SECTION_COUNT)
        return null;
    return `afterSection${sectionNumber}`;
}
/** Bounds for the "quote viewed" cooldown (0 = notify on every open). */
export const QUOTE_VIEWED_COOLDOWN_MIN = 0;
export const QUOTE_VIEWED_COOLDOWN_MAX = 1440;
export const QUOTE_VIEWED_COOLDOWN_DEFAULT = 60;
//# sourceMappingURL=quote.js.map